import React, { FunctionComponent } from 'react';
import { Provider } from 'react-redux';
import { Router, Route, IndexRedirect, IndexRoute, Redirect, RouteComponent } from 'react-router';

import 'normalize.css';
import './style/global.css';

import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import {
  requireAuth,
  redirectFromIndexRoute,
  setLastViewedTab,
  requireGroupMembership,
  asyncRequireAuth,
} from './internals/wireUpInternals';
import { withUserInvited } from './containers/UserInvitedMessage.container';
import { withConnectToSubscriptionMessages } from './hoc/withConnectToSubscriptionMessages';

import Auth from './pages/auth';
import { SubscriptionPage } from './pages/Subscription.page';
import User from './pages/user';
import NotFound from './pages/notfound';
import { ServerErrorPage } from './pages/ServerError.page';

import { AppRoot } from './components/app-root/AppRoot';
import { DashboardPage } from './pages/dashboard/Dashboard.page';
import { CreateSubscription } from './pages/create-subscription/CreateSubscription';
import AuthLogin from './auth/login';
import AuthLogout from './auth/logout';
import AuthSignup from './auth/signup';
import AuthForgotPassword from './auth/forgot-password';
import AuthResetPassword from './auth/reset-password';
import AuthVerifyEmail from './auth/verify-email';
import AuthApproveTos from './pages/approve_tos';

import { FlowPage } from './pages/flow/Flow.page';
import { FlowDashboardPage } from './pages/FlowDashboard.page';
import FlowConfigPage from './pages/flow/flow-config/FlowConfigPage';
import { FlowInputPage } from './pages/flow/flow-input/FlowInput.page';
import { FlowOutputPage } from './pages/flow/flow-output/FlowOutput.page';

import SubOverview from './subscription/pages/overview/Overview';
import { PipesList } from './containers/PipesList.container';
import SubPipeListPermissions from './subscription/pages/pipes/PipesPermissions';
import SubPipeDashboard from './components/pipe-dashboard/PipeDashboard';
import SubEditPipe from './subscription/pages/pipes/EditPipe';
import { PipeInput } from './containers/PipeInput.container';
import { PipeOutput } from './components/pipe-output/PipeOutput.container';
import PipeInferredSchema from './components/pipe-inferred-schema/PipeInferredSchema';
import { PipeDeadLetters } from './components/pipe-dead-letters/PipeDeadLetters.container';
import SubNewPipe from './subscription/pages/pipes/NewPipe';
import PipeGraph from './subscription/pages/pipes/PipeGraph';
import { PipeExecutionLog } from './subscription/pages/pipes/PipeExecutionLog';
import { PipePermissions } from './subscription/pages/pipes/PipePermissions.container';
import SubPipePrototypePermissions from './subscription/pages/pipes/PipesPrototypePermissions';
import SubPipeGlobalOverview from './subscription/pages/pipes/PipeOverview';
import { PipeDebug } from './subscription/pages/pipes/PipeDebug.container';
import SubPipeIndexes from './components/pipe-indexes/PipeIndexes';
import SubDataset from './subscription/pages/datasets/Dataset';
import { DatasetInspectorContainer } from './components/dataset-inspector/DatasetInspectorContainer';
import SubDatasetPermissions from './subscription/pages/datasets/DatasetPermissions';
import { DatasetIndexes as SubDatasetIndexes } from './subscription/pages/datasets/DatasetIndexes';

import { PipeDeletedContainer } from './containers/PipeDeleted.container';
import { SystemDeletedContainer } from './containers/SystemDeleted.container';
import { InternalPipesContainer } from './containers/InternalPipes.container';
import { InternalSystemsContainer } from './containers/InternalSystems.container';

import { SystemsList } from './containers/SystemsList.container';
import SubSystemListPermissions from './subscription/pages/systems/SystemsPermissions';
import SubSystemPrototypePermissions from './subscription/pages/systems/SystemsPrototypePermissions';
import SubNewSystem from './subscription/pages/systems/NewSystem';
import EditSystem from './subscription/pages/systems/EditSystem';
import SystemStatusPage from './subscription/pages/systems/SystemStatus';
import SystemOverview from './subscription/pages/systems/SystemOverview';
import SystemSecretsPage from './subscription/pages/systems/SystemSecrets';
import SubSystemPermissions from './subscription/pages/systems/SystemPermissions';
import SystemGraph from './subscription/pages/systems/SystemGraph';
import SubSystemDashboard from './components/system-dashboard/SystemDashboard';

import SearchPage from './components/search/SearchPage';
import ModelsListPage from './components/models-list/ModelsListPage';
import EntityTypesListPage from './components/entity-types-list/EntityTypesListPage';
import EntityTypeOverview from './components/entity-type-overview/EntityTypeOverview';
import EntityTypePage from './components/entity-type-page/EntityTypePage';
import EntityTypePropertyPage from './components/entity-type-property-page';
import EntityTypePropertyOverview from './components/entity-type-property-overview';
import ModelPage from './components/model-page/ModelPage';
import ModelOverview from './components/model-overview/ModelOverview';

import { FlowsList } from './containers/FlowsList.container';

import { SwitchSubscription } from './containers/SwitchSubscription.container';
import { SubscriptionSettingsPage } from './pages/SubscriptionSettings.page';

import DhSettingsQueues from './subscription/pages/settings-datahub-queues';
import { UserDatasets } from './containers/UserDatasets.container';
import { InternalDatasets } from './containers/InternalDatasets.container';
import { SettingsDatahubLogs } from './components/settings-datahub-logs/SettingsDatahubLogs.container';
import { SettingsVariables } from './subscription/pages/settings-datahub-variables/SettingsVariables.container';
import DhSettingsMetadata from './subscription/pages/settings-datahub-metadata';
import DhSettingsTools from './subscription/pages/settings-datahub-tools';
import DhSettingsPermissions from './subscription/pages/settings-datahub-permissions';
import { StacktracePage } from './pages/datahub/stacktrace/StacktracePage.container';

import { SettingsBasicsContainer } from './containers/SettingsBasic.container';
import SubSettingsMembers from './subscription/pages/settings-subscription-members';
import { SettingsSubscriptionProducts } from './subscription/pages/settings-subscription-products/SettingsSubscriptionProducts';
import SubSettingsVpn from './subscription/pages/settings-subscription-vpn';
import { SettingsSubscriptionDns } from './subscription/pages/settings-subscription-dns/SettingsSubscriptionDns';
import { SettingsSubscriptionNetwork } from './containers/SettingsSubscriptionNetwork.container';
import SubSettingsRoles from './subscription/pages/settings-subscription-roles';
import SubSettingsLicense from './subscription/pages/settings-subscription-license';
import SubSettingsJwt from './subscription/pages/settings-subscription-jwt';

import UserProfile from './user/pages/profile';
import UserChangePassword from './user/pages/change-password';
import { PaymentMethodsPage } from './user/pages/payment-methods/PaymentMethods.page';
import { CreatePaymentMethod } from './user/pages/payment-methods/create';
import { EditPaymentMethod } from './user/pages/payment-methods/edit';
import NIInspectorPage from './components/ni-inspector-page/NIInspectorPage';
import { sendHibernationAnalyticsTrigger } from './utils/analytics.utils';
import {
  onEnterLoadCoreData,
  preLoadData,
  preLoadSubscriptions,
} from './internals/onEnterFunctions';

import { RoutesConstants } from './constants/routes.constants';

import type { StoreType, HistoryWithStoreType } from 'Internals/store';

const renderIfSubMember = withUserInvited; // Just an alias
const renderIfSubConnected = (component: React.ReactNode | unknown | FunctionComponent<any>) => {
  // @ts-ignore
  return withUserInvited(withConnectToSubscriptionMessages(component));
};

export const AppRoutes: React.FC<{ store: StoreType; history: HistoryWithStoreType }> = ({
  store,
  history,
}) => {
  // Sending an analytics signal to the portal on a subscription page when the user refreshes the browser.
  const portalUrl = store.getState().globals.portalUrl;
  sendHibernationAnalyticsTrigger(portalUrl);

  return (
    <Provider store={store}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <Router history={history}>
          <Route component={AppRoot as unknown as RouteComponent}>
            <Redirect from="/" to="dashboard" />
            <Route path="dashboard" component={DashboardPage} onEnter={requireAuth} />
            <Route
              path="create-subscription"
              component={CreateSubscription as unknown as RouteComponent}
              onEnter={requireAuth}
            />
            <Route path="auth" component={Auth}>
              <Route path="login" component={AuthLogin} />
              <Route path="signup" component={AuthSignup as unknown as RouteComponent} />
              <Route
                path="forgot-password"
                component={AuthForgotPassword as unknown as RouteComponent}
              />
              <Route
                path="reset-password"
                component={AuthResetPassword as unknown as RouteComponent}
              />
              <Route path="logout" component={AuthLogout} />
              <Route path="verify-emailaddress" component={AuthVerifyEmail} />
            </Route>

            <Route path="approve-tos" component={AuthApproveTos} onEnter={requireAuth} />

            <Route path="user" component={User} onEnter={requireAuth}>
              <IndexRedirect to="profile" />
              <Route path="profile">
                <IndexRoute component={UserProfile as unknown as RouteComponent} />
                <Route path="change-password" component={UserChangePassword} />
              </Route>
              <Route path="payment-methods">
                <IndexRoute component={PaymentMethodsPage as unknown as RouteComponent} />
                <Route path="create" component={CreatePaymentMethod as unknown as RouteComponent} />
                <Route path="edit/:pmID" component={EditPaymentMethod} />
              </Route>
            </Route>

            <Route
              path="switch-to-subscription/:subId"
              component={SwitchSubscription as unknown as RouteComponent}
            />

            <Route
              path="subscription/:subId"
              component={SubscriptionPage}
              onEnter={async (route, replace, callback) => {
                await asyncRequireAuth({ nextState: route, replace });
                await preLoadSubscriptions(route.params.subId, callback);
              }}
            >
              <IndexRedirect to="overview" />
              <Route
                path="overview"
                component={renderIfSubConnected(SubOverview) as unknown as RouteComponent}
              />
              {/* the appliance redirect to this url, so don't remove it */}
              <Redirect from="connect" to="settings/network" />
              <Route path="pipes">
                <IndexRoute
                  onEnter={(route, replace, callback) =>
                    redirectFromIndexRoute(
                      'pipes',
                      'all',
                      `/subscription/${route.params.subId}/pipes`,
                      replace,
                      callback
                    )
                  }
                />
                <Route
                  path="all"
                  component={renderIfSubConnected(PipesList) as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => setLastViewedTab('pipes', 'all', callback)}
                />
                <Route
                  path="new(/:pipeID)"
                  component={renderIfSubConnected(SubNewPipe) as unknown as RouteComponent}
                />
                <Route
                  path="permissions"
                  component={
                    renderIfSubConnected(SubPipeListPermissions) as unknown as RouteComponent
                  }
                  onEnter={(route, replace, callback) =>
                    setLastViewedTab('pipes', 'permissions', callback)
                  }
                />
                <Route
                  path="prototype-permissions"
                  component={
                    renderIfSubConnected(SubPipePrototypePermissions) as unknown as RouteComponent
                  }
                  onEnter={(route, replace, callback) =>
                    setLastViewedTab('pipes', 'prototype-permissions', callback)
                  }
                />
                <Route
                  path="internal-pipes"
                  component={
                    renderIfSubConnected(InternalPipesContainer) as unknown as RouteComponent
                  }
                  onEnter={(_route, _replace, callback) =>
                    setLastViewedTab('pipes', 'internal-pipes', callback)
                  }
                />
                <Route
                  path={RoutesConstants.PipeID}
                  component={
                    renderIfSubConnected(PipeDeletedContainer) as unknown as RouteComponent
                  }
                >
                  <IndexRoute
                    onEnter={(route, replace, callback) =>
                      redirectFromIndexRoute(
                        'pipe',
                        'dashboard',
                        `/subscription/${route.params.subId}/pipes/pipe/${route.params.pipeID}`,
                        replace,
                        callback
                      )
                    }
                  />
                  <Route
                    path="dashboard"
                    component={SubPipeDashboard}
                    onEnter={(_route, _replace, callback) =>
                      setLastViewedTab('pipe', 'dashboard', callback)
                    }
                  />
                  <Route
                    path="edit"
                    component={SubEditPipe}
                    onEnter={(_route, _replace, callback) =>
                      setLastViewedTab('pipe', 'edit', callback)
                    }
                  />
                  <Route
                    path="input"
                    component={PipeInput as unknown as RouteComponent}
                    onEnter={(_route, _replace, callback) =>
                      setLastViewedTab('pipe', 'input', callback)
                    }
                  />
                  <Route
                    path="output"
                    component={PipeOutput}
                    onEnter={(_route, _replace, callback) => {
                      setLastViewedTab('pipe', 'output', callback);
                    }}
                  />
                  <Route
                    path="inferred-schema"
                    component={PipeInferredSchema}
                    onEnter={(_route, _replace, callback) => {
                      setLastViewedTab('pipe', 'inferred-schema', callback);
                    }}
                  />
                  <Route
                    path="dead-letters"
                    component={PipeDeadLetters as RouteComponent}
                    onEnter={(_route, _replace, callback) => {
                      setLastViewedTab('pipe', 'dead-letters', callback);
                    }}
                  />
                  <Route
                    path="graph"
                    component={PipeGraph}
                    onEnter={(_route, _replace, callback) =>
                      setLastViewedTab('pipe', 'graph', callback)
                    }
                  />
                  <Redirect from="flow" to="graph" />
                  <Route
                    path="permissions"
                    component={PipePermissions as RouteComponent}
                    onEnter={(_route, _replace, callback) =>
                      setLastViewedTab('pipe', 'permissions', callback)
                    }
                  />
                  <Route
                    path="overview"
                    component={SubPipeGlobalOverview}
                    onEnter={(_route, _replace, callback) =>
                      setLastViewedTab('pipe', 'overview', callback)
                    }
                  />
                  <Route
                    path="execution"
                    component={PipeExecutionLog as unknown as RouteComponent}
                    onEnter={(_route, _replace, callback) =>
                      setLastViewedTab('pipe', 'execution', callback)
                    }
                  />
                  <Route
                    path="indexes"
                    component={SubPipeIndexes}
                    onEnter={(_route, _replace, callback) => {
                      setLastViewedTab('pipe', 'indexes', callback);
                    }}
                  />
                  <Route
                    path="debug"
                    component={PipeDebug as unknown as RouteComponent}
                    onEnter={(_route, _replace, callback) =>
                      setLastViewedTab('pipe', 'debug', callback)
                    }
                  />
                </Route>
              </Route>
              <Route path="systems">
                <IndexRoute
                  onEnter={(route, replace, callback) =>
                    redirectFromIndexRoute(
                      'systems',
                      'all',
                      `/subscription/${route.params.subId}/systems`,
                      replace,
                      callback
                    )
                  }
                />
                <Route
                  path="all"
                  component={renderIfSubConnected(SystemsList) as unknown as RouteComponent}
                  onEnter={(_route, _replace, callback) => {
                    setLastViewedTab('systems', 'all', callback);
                  }}
                />
                <Route
                  path="new(/:systemID)"
                  component={renderIfSubConnected(SubNewSystem) as unknown as RouteComponent}
                />
                <Route
                  path="permissions"
                  component={
                    renderIfSubConnected(SubSystemListPermissions) as unknown as RouteComponent
                  }
                  onEnter={(_route, _replace, callback) => {
                    setLastViewedTab('systems', 'permissions', callback);
                  }}
                />
                <Route
                  path="prototype-permissions"
                  component={
                    renderIfSubConnected(SubSystemPrototypePermissions) as unknown as RouteComponent
                  }
                  onEnter={(_route, _replace, callback) => {
                    setLastViewedTab('systems', 'prototype-permissions', callback);
                  }}
                />
                <Route
                  path="internal-systems"
                  component={
                    renderIfSubConnected(InternalSystemsContainer) as unknown as RouteComponent
                  }
                  onEnter={(_route, _replace, callback) => {
                    setLastViewedTab('systems', 'internal-systems', callback);
                  }}
                />
                <Route
                  path="system/:systemID"
                  component={
                    renderIfSubConnected(SystemDeletedContainer) as unknown as RouteComponent
                  }
                >
                  <IndexRoute
                    onEnter={(route, replace, callback) =>
                      redirectFromIndexRoute(
                        'system',
                        'dashboard',
                        `/subscription/${route.params.subId}/systems/system/${route.params.systemID}`,
                        replace,
                        callback
                      )
                    }
                  />
                  <Route
                    path="dashboard"
                    component={SubSystemDashboard}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('system', 'dashboard', callback);
                    }}
                  />
                  <Route
                    path="status"
                    component={SystemStatusPage}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('system', 'status', callback);
                    }}
                  />
                  <Route
                    path="secrets"
                    component={SystemSecretsPage}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('system', 'secrets', callback);
                    }}
                  />
                  <Route
                    path="edit"
                    component={EditSystem}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('system', 'edit', callback);
                    }}
                  />
                  <Route
                    path="graph"
                    component={SystemGraph}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('system', 'graph', callback);
                    }}
                  />
                  <Redirect from="flow" to="graph" />
                  <Route
                    path="overview"
                    component={SystemOverview}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('system', 'overview', callback);
                    }}
                  />
                  <Route
                    path="permissions"
                    component={SubSystemPermissions}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('system', 'permissions', callback);
                    }}
                  />
                </Route>
              </Route>
              <Redirect from="search" to="browse" />
              <Redirect from="data-catalog" to="browse" />
              <Route path="browse">
                <IndexRoute
                  onEnter={(route, replace, callback) =>
                    redirectFromIndexRoute(
                      'browse',
                      'search',
                      `/subscription/${route.params.subId}/browse`,
                      replace,
                      callback
                    )
                  }
                />
                <Route
                  path="search"
                  component={renderIfSubConnected(SearchPage) as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('browse', 'search', callback);
                  }}
                />
                <Route
                  path="ni/:ni"
                  component={renderIfSubConnected(NIInspectorPage) as unknown as RouteComponent}
                />
                <Route
                  path="entity-types"
                  component={renderIfSubConnected(EntityTypesListPage) as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('browse', 'entity-types', callback);
                  }}
                />
                <Route
                  path="models"
                  component={renderIfSubConnected(ModelsListPage) as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('browse', 'models', callback);
                  }}
                />
                <Route
                  path="models/:modelId"
                  component={renderIfSubConnected(ModelPage) as unknown as RouteComponent}
                >
                  <IndexRoute
                    onEnter={(route, replace, callback) =>
                      redirectFromIndexRoute(
                        'model',
                        'overview',
                        `/subscription/${route.params.subId}/browse/models/${route.params.modelId}`,
                        replace,
                        callback
                      )
                    }
                  />
                  <Route
                    path="overview"
                    component={ModelOverview}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('model', 'overview', callback);
                    }}
                  />
                </Route>
                <Route
                  path="entity-types/:modelId/:entityTypeId"
                  component={renderIfSubConnected(EntityTypePage) as unknown as RouteComponent}
                >
                  <IndexRoute
                    onEnter={(route, replace, callback) =>
                      redirectFromIndexRoute(
                        'entity-type',
                        'overview',
                        `/subscription/${route.params.subId}/browse/entity-types/${route.params.modelId}/${route.params.entityTypeId}`,
                        replace,
                        callback
                      )
                    }
                  />
                  <Route
                    path="overview"
                    component={EntityTypeOverview}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('data-type', 'overview', callback);
                    }}
                  />
                </Route>
                <Route
                  path="properties/:propertyId"
                  component={
                    renderIfSubConnected(EntityTypePropertyPage) as unknown as RouteComponent
                  }
                >
                  <IndexRoute
                    onEnter={(route, replace, callback) =>
                      redirectFromIndexRoute(
                        'entity-type-property',
                        'overview',
                        `/subscription/${route.params.subId}/browse/properties/${route.params.propertyId}`,
                        replace,
                        callback
                      )
                    }
                  />
                  <Route
                    path="overview"
                    component={EntityTypePropertyOverview}
                    onEnter={(route, replace, callback) => {
                      setLastViewedTab('data-type', 'overview', callback);
                    }}
                  />
                </Route>
              </Route>
              <Route path="flows">
                <IndexRoute
                  onEnter={(route, replace, callback) =>
                    redirectFromIndexRoute(
                      'flows',
                      'all',
                      `/subscription/${route.params.subId}/flows`,
                      replace,
                      callback
                    )
                  }
                />
                <Route
                  path="all"
                  component={renderIfSubConnected(FlowsList) as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('flows', 'all');
                    onEnterLoadCoreData(
                      route,
                      replace,
                      callback
                    )(store.getState().subscription.connected);
                  }}
                />

                <Route
                  path="flow"
                  component={renderIfSubConnected(FlowPage) as unknown as RouteComponent}
                >
                  <IndexRedirect to="dashboard" />
                  <Route path="dashboard" component={FlowDashboardPage} />
                  <Route path="config" component={FlowConfigPage} />
                  <Route path="input" component={FlowInputPage as RouteComponent} />
                  <Route path="output" component={FlowOutputPage as RouteComponent} />
                </Route>
              </Route>
              <Route
                path="settings-subscription"
                component={renderIfSubMember(SubscriptionSettingsPage) as unknown as RouteComponent}
                onEnter={(route, _replace, callback) => {
                  preLoadData(route.params.subId, callback);
                }}
              >
                <IndexRoute
                  onEnter={(route, replace, callback) =>
                    redirectFromIndexRoute(
                      'settings-subscription',
                      'basics',
                      `/subscription/${route.params.subId}/settings-subscription`,
                      replace,
                      callback
                    )
                  }
                />
                <Route
                  path="basics"
                  component={SettingsBasicsContainer}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'basics', callback);
                  }}
                />
                <Route
                  path="license"
                  component={SubSettingsLicense as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'license', callback);
                  }}
                />
                <Route
                  path="network"
                  component={SettingsSubscriptionNetwork as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'network', callback);
                  }}
                />
                <Route
                  path="products"
                  component={SettingsSubscriptionProducts as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'products', callback);
                  }}
                />
                <Route
                  path="vpn"
                  component={SubSettingsVpn}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'vpn', callback);
                  }}
                />
                <Route
                  path="dns"
                  component={SettingsSubscriptionDns as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'dns', callback);
                  }}
                />
                <Route
                  path="members"
                  component={SubSettingsMembers as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'members', callback);
                  }}
                />
                <Route
                  path="roles"
                  component={SubSettingsRoles as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'roles', callback);
                  }}
                />
                <Route
                  path="jwt"
                  component={SubSettingsJwt as unknown as RouteComponent}
                  onEnter={(route, replace, callback) => {
                    setLastViewedTab('settings-subscription', 'jwt', callback);
                  }}
                />
              </Route>
              <Route path="settings-datahub">
                <IndexRoute
                  onEnter={(route, replace, callback) =>
                    redirectFromIndexRoute(
                      'settings-datahub',
                      'queues',
                      `/subscription/${route.params.subId}/settings-datahub`,
                      replace,
                      callback
                    )
                  }
                />
                <Route
                  path="queues"
                  component={renderIfSubConnected(DhSettingsQueues) as unknown as RouteComponent}
                  onEnter={(_route, _replace, callback) => {
                    setLastViewedTab('settings-datahub', 'queues', callback);
                  }}
                />
                <Route
                  path="user-datasets"
                  component={renderIfSubConnected(UserDatasets) as unknown as RouteComponent}
                  onEnter={(_route, _replace, callback) => {
                    setLastViewedTab('settings-datahub', 'user-datasets', callback);
                  }}
                />
                <Route
                  path="internal-datasets"
                  component={renderIfSubConnected(InternalDatasets) as unknown as RouteComponent}
                  onEnter={(_route, _replace, callback) => {
                    setLastViewedTab('settings-datahub', 'internal-datasets', callback);
                  }}
                />
                <Route
                  path="dataset/:datasetID"
                  component={renderIfSubConnected(SubDataset) as unknown as RouteComponent}
                >
                  <IndexRoute
                    onEnter={(route, replace, callback) =>
                      redirectFromIndexRoute(
                        'dataset',
                        'view',
                        `/subscription/${route.params.subId}/settings-datahub/dataset/${route.params.datasetID}`,
                        replace,
                        callback
                      )
                    }
                  />
                  <Redirect from="overview" to="view" />
                  <Route
                    path="view"
                    component={DatasetInspectorContainer as unknown as RouteComponent}
                    onEnter={(_route, _replace, callback) => {
                      setLastViewedTab('dataset', 'view', callback);
                    }}
                  />
                  <Route
                    path="permissions"
                    component={SubDatasetPermissions}
                    onEnter={(_route, replace, callback) => {
                      setLastViewedTab('dataset', 'permissions', callback);
                    }}
                  />
                  <Route
                    path="indexes"
                    component={SubDatasetIndexes}
                    onEnter={(_route, replace, callback) => {
                      setLastViewedTab('dataset', 'indexes', callback);
                    }}
                  />
                </Route>
                <Route
                  path="logs"
                  component={renderIfSubConnected(SettingsDatahubLogs) as unknown as RouteComponent}
                  onEnter={(_route, replace, callback) => {
                    setLastViewedTab('settings-datahub', 'logs', callback);
                  }}
                />
                <Route
                  path="tools"
                  component={renderIfSubConnected(DhSettingsTools) as unknown as RouteComponent}
                  onEnter={(_route, replace, callback) => {
                    setLastViewedTab('settings-datahub', 'tools', callback);
                  }}
                />
                <Route
                  path="variables"
                  component={renderIfSubConnected(SettingsVariables) as unknown as RouteComponent}
                  onEnter={(_route, replace, callback) => {
                    setLastViewedTab('settings-datahub', 'variables', callback);
                  }}
                />
                <Route
                  path="metadata"
                  component={renderIfSubConnected(DhSettingsMetadata) as unknown as RouteComponent}
                  onEnter={(_route, replace, callback) => {
                    setLastViewedTab('settings-datahub', 'metadata', callback);
                  }}
                />
                <Route
                  path="permissions"
                  component={
                    renderIfSubConnected(DhSettingsPermissions) as unknown as RouteComponent
                  }
                  onEnter={(_route, replace, callback) => {
                    setLastViewedTab('settings-datahub', 'permissions', callback);
                  }}
                />
                <Route
                  path="stacktrace"
                  component={renderIfSubConnected(StacktracePage) as unknown as RouteComponent}
                  onEnter={(route, replace, callback) =>
                    requireGroupMembership(
                      'group:Admin',
                      `/subscription/${route.params.subId}/settings-datahub/`,
                      replace,
                      callback
                    )
                  }
                />
              </Route>
            </Route>
            <Route path="server-error" component={ServerErrorPage} />
            <Route path="*" component={NotFound} />
          </Route>
        </Router>
      </MuiPickersUtilsProvider>
    </Provider>
  );
};
